/**
 * dashboard-route.js — client-saas /home
 *
 * Translation of the MachFiveMagnet demo dashboard (demo/index.html .demo-content)
 * into our shell: dark side-nav + light content. Built entirely from the tokenized
 * primitive kit (styles/dashboard-kit.css) — no inline styles, no demo --m5-* vars.
 *
 * Data posture: one owner-scoped roll-up (GET /m5t/v5/dashboard/stats) feeds the KPI
 * tiles, the opens↔leads trend, the completion funnel (m5mMagnetEvent beacons), CTA
 * usage, top campaigns, and the recent-interactions feed; Recent Leads still reads
 * m5mLead for the person-level list. Anything without data renders an honest
 * empty-state — never a fabricated number. Charts are hand-rolled inline SVG (the
 * single-bundle build makes chart libs prohibitively heavy — see ad-performance).
 */

import AuthController from '../services/AuthController.js';
import { inPortfolio, ensurePortfolioStyles, clientsTableHTML, wireClientRows, pickerSkeletonHTML } from '../services/portfolio-view.js';
import { guardedFetch } from '../core/navGeneration.js';
import { isDenial, denialState, failIfNotOk } from '../core/denialSurface.js';

// The five metric columns every portfolio row always carries; renderPortfolio
// adds data-dependent columns (A/B, Booked, Called, Spend) on top. The
// skeleton uses only these headers - extra columns change width, not height,
// so the reserved vertical space stays exact.
const PF_BASE_COLS = [
  { h: 'Magnets' }, { h: 'Leads · wk' }, { h: 'Leads · total' }, { h: 'Opens' }, { h: 'Compl.' },
];
import { HANDOFF, setHandoff, setLeadsHandoff, leadsHandoffScope } from '../core/routeHandoff.js';
let drillRange = {}, drillScope;
const dashboardLeads = context => setLeadsHandoff({ ...(drillScope || leadsHandoffScope()), ...context, returnTo: { hash: '#/home', label: 'Back to dashboard' } });
import { statCard } from '../core/statCard.js';
import { ensureChartStyles, dualAxisChartHTML, wireChartHover } from '../services/chart-kit.js';
import { capList } from '../core/listExpand.js';
import { quotaState, fetchMyPlan } from '../services/planClient.js';
import { PLANS } from '@sse/shared';

const emptyState = (icon, msg) =>
  `<div class="m5t-empty"><span class="material-symbols-outlined" aria-hidden="true">${icon}</span><p>${msg}</p></div>`;

const esc = (s) => String(s ?? '').replace(/[&<>"']/g, (c) => ({ '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;', "'": '&#39;' }[c]));
const fmtNum = (n) => Number(n || 0).toLocaleString();
const leadName = (l) => [l.first_name, l.last_name].filter(Boolean).join(' ').trim() || l.email || '(no name)';
const leadInitials = (l) => ((leadName(l).split(/\s+/).slice(0, 2).map((w) => w[0] || '').join('')) || '?').toUpperCase();
function leadWhen(l) {
  const tz = l.created?.created_tz;
  if (!tz) return '';
  try { return new Date(tz).toLocaleDateString([], { month: 'short', day: 'numeric' }); } catch { return ''; }
}
function relTime(iso) {
  try {
    const s = Math.max(0, (Date.now() - new Date(iso).getTime()) / 1000);
    if (s < 60) return 'just now';
    if (s < 3600) return `${Math.floor(s / 60)}m ago`;
    if (s < 86400) return `${Math.floor(s / 3600)}h ago`;
    return `${Math.floor(s / 86400)}d ago`;
  } catch { return ''; }
}

let stylesInjected = false;
function ensureStyles() {
  // Shared chart CSS is injected independently of this route's own styles — three routes
  // need it and only the first to render should pay for it.
  ensureChartStyles();
  if (stylesInjected) return;
  const s = document.createElement('style');
  s.id = 'm5t-dash-recent-styles';
  s.textContent = `
    .page-shell .recent-lead { display:flex; align-items:center; gap:10px; padding:10px 4px; border-bottom:1px solid var(--color-surface-2,#EAF0F7); text-decoration:none; color:inherit; }
    .page-shell .recent-lead:last-child { border-bottom:0; }
    .page-shell .recent-lead:hover { background:var(--color-surface,#F7FAFD); }
    .page-shell .recent-lead__ava { flex:0 0 auto; width:34px; height:34px; border-radius:50%; display:grid; place-items:center; font-weight:700; font-size:0.74rem; color:#fff; background:var(--color-primary-600,#0072BA); }
    .page-shell .recent-lead__main { flex:1 1 auto; min-width:0; display:flex; flex-direction:column; }
    .page-shell .recent-lead__name { font-weight:600; font-size:0.9rem; overflow:hidden; text-overflow:ellipsis; white-space:nowrap; }
    .page-shell .recent-lead__sub { font-size:0.78rem; color:var(--color-text-subtle,#6C7C9A); overflow:hidden; text-overflow:ellipsis; white-space:nowrap; }
    /* Colors come from ui-kit .m5t-badge[data-s] (dark-mode aware); this rule is layout only. */
    .page-shell .recent-lead__badge { flex:0 0 auto; }
    .page-shell .recent-lead__time { flex:0 0 auto; font-size:0.74rem; color:var(--color-text-subtle,#6C7C9A); }

    /* Chart marks, tooltip and hit bands now live in services/chart-kit.js (.m5c-*),
       shared with ad-performance and analytics. The funnel/list rules below are a
       different component family and stay here. */

    .page-shell .dash-funnel { display:flex; flex-direction:column; gap:12px; padding:6px 2px; }
    .page-shell .dash-funnel__row { display:flex; flex-direction:column; gap:4px; }
    .page-shell .dash-funnel__meta { display:flex; justify-content:space-between; font-size:0.8rem; }
    .page-shell .dash-funnel__label { font-weight:600; color:var(--color-text-muted,#4A5A78); }
    .page-shell .dash-funnel__val { font-weight:700; color:var(--color-text,#0B1220); }
    .page-shell .dash-funnel__track { height:12px; border-radius:999px; background:var(--color-surface-2,#EAF0F7); overflow:hidden; }
    .page-shell .dash-funnel__bar { height:100%; border-radius:999px; background:var(--chart-1,#0072BA); transition:width .4s ease; }
    .page-shell .dash-funnel__bar--mid { background:var(--color-info,#0D9488); }
    .page-shell .dash-funnel__bar--end { background:var(--color-success,#009473); }
    .page-shell .dash-funnel__pct { font-size:0.72rem; color:var(--color-text-subtle,#6C7C9A); }

    .page-shell .dash-list { display:flex; flex-direction:column; }
    .page-shell .dash-list__row { display:flex; align-items:center; gap:10px; padding:9px 4px; border-bottom:1px solid var(--color-surface-2,#EAF0F7); font-size:0.86rem; }
    /* Restated because the author display rule above beats the UA's [hidden] rule,
       and core/listExpand.js hides capped rows with setAttribute('hidden'). Without
       this the cap is a no-op: the control reads "Showing 25 of N" above N visible
       rows. Same failure deliberate-fixed once already at deliverable-fill-route.js. */
    .page-shell .dash-list__row[hidden] { display: none; }
    .page-shell .dash-list__row:last-child { border-bottom:0; }
    .page-shell .dash-list__ico { flex:0 0 auto; width:28px; height:28px; border-radius:8px; display:grid; place-items:center; background:var(--color-accent-soft,rgba(0,114,186,0.1)); color:var(--color-accent,#0072BA); }
    .page-shell .dash-list__ico .material-symbols-outlined { font-size:17px; }
    .page-shell .dash-list__main { flex:1 1 auto; min-width:0; overflow:hidden; text-overflow:ellipsis; white-space:nowrap; }
    .page-shell .dash-list__sub { display:block; color:var(--color-text-subtle,#6C7C9A); font-size:.74rem; font-weight:400; }
    .page-shell .dash-list__num { flex:0 0 auto; font-weight:700; }
    .page-shell .dash-list__time { flex:0 0 auto; font-size:0.74rem; color:var(--color-text-subtle,#6C7C9A); }
    .page-shell .dash-list__mode { flex:0 0 auto; font-size:0.66rem; font-weight:700; letter-spacing:0.03em; padding:2px 7px; border-radius:999px; background:var(--color-surface-2,#EAF0F7); color:var(--color-text-muted,#4A5A78); }
    /* Drill-through rows: same row shape, but they announce themselves as links. */
    .page-shell .dash-list__row--link { text-decoration:none; color:inherit; border-radius:8px; transition:background .14s ease; }
    .page-shell .dash-list__row--link:hover { background:var(--color-surface,#F7FAFD); }
    .page-shell .dash-list__row--link:hover .dash-list__main { color:var(--color-primary-600,#0072BA); }
    .page-shell .dash-list__go { flex:0 0 auto; font-size:18px; color:var(--color-text-subtle,#6C7C9A); opacity:0; transition:opacity .14s ease; }
    .page-shell .dash-list__row--link:hover .dash-list__go, .page-shell .dash-list__row--link:focus-visible .dash-list__go { opacity:1; }
    /* Touch has no hover — keep the affordance visible there. */
    @media (hover: none) { .page-shell .dash-list__go { opacity:1; } }
    html.ff-no-animations .page-shell .dash-list__row--link, html.ff-no-animations .page-shell .dash-list__go { transition:none; }
    @media (prefers-reduced-motion: reduce) { .page-shell .dash-list__row--link, .page-shell .dash-list__go { transition:none; } }

    /* card-header is a two-child space-between; this keeps the Live pill and the
       view-all link together on the right instead of pushing the title off-centre. */
    .page-shell .card-header__aside { display:inline-flex; align-items:center; gap:14px; }

  `;
  document.head.appendChild(s);
  stylesInjected = true;
}

function recentLeadHTML(l) {
  return `<a class="recent-lead" href="#/leads" data-lead-id="${esc(l.id || '')}" data-lead-guid="${esc(l.guid || '')}" data-owner-guid="${esc(l.owner_guid || '')}">
    <span class="recent-lead__ava">${esc(leadInitials(l))}</span>
    <span class="recent-lead__main">
      <span class="recent-lead__name">${esc(leadName(l))}</span>
      <span class="recent-lead__sub">${esc(l.email || l.phone || '')}</span>
    </span>
    <span class="m5t-badge recent-lead__badge" data-s="${esc(l.status)}">${esc(l.status)}</span>
    <span class="recent-lead__time">${esc(leadWhen(l))}</span>
  </a>`;
}

// The card itself now lives in core/statCard.js — it was one of three copies, and the
// other two (analytics, ad-performance) had no href option at all, which is why "make the
// summary boxes clickable" kept arriving as a per-page request. Re-exported here because
// this module's own tests import it from this path.
//
// href stays OPT-IN per card: a KPI that clicks through to a page which cannot show the
// rows behind the number is a dead end, not a fix.
//
// Opens and Completion Rate were unlinked because /analytics was a SECTION_ROUTES
// placeholder — the note said they "should gain an href when Analytics becomes a real
// view, not before". It is one now, and its by-magnet table carries the Opens and
// Completed columns those two numbers summarise, so the stated precondition is met.
export { statCard };

const chartCard = (icon, title, bodyId, { wide = false, full = false, controls = false } = {}) => `
  <div class="dashboard-card${wide ? ' chart-card-wide' : ''}${full ? ' chart-card-full' : ''}">
    <div class="card-header">
      <h2 class="card-title"><span class="material-symbols-outlined" aria-hidden="true">${icon}</span> ${title}</h2>
      ${controls ? `<div class="chart-controls" id="dash-periods">
        <button class="chart-period-btn" type="button" data-days="7">7 Days</button>
        <button class="chart-period-btn active" type="button" data-days="30">30 Days</button>
      </div>` : ''}
    </div>
    <div class="card-body" id="${bodyId}">${emptyState('hourglass_empty', 'Loading…')}</div>
  </div>`;

let periodDays = 30;

export function render() {
  ensureStyles();
  ensurePortfolioStyles();
  const portfolio = inPortfolio();
  return `
    <div class="page-shell" data-section="home">
      <div class="page-header">
        <div class="page-header-text">
          <h1 class="page-title">${portfolio ? 'Portfolio' : 'Overview'}</h1>
          ${portfolio ? '' : '<p class="page-description">Your magnets, conversations, and lead performance at a glance.</p>'}
        </div>
      </div>

      ${portfolio ? `
      <div class="dashboard-card dashboard-card--lead">
        <div class="card-header">
          <h2 class="card-title"><span class="material-symbols-outlined" aria-hidden="true">groups</span> Clients</h2>
          <a class="view-all-link" href="#/clients">Manage clients →</a>
        </div>
        <div class="card-body" id="dash-portfolio">${pickerSkeletonHTML(PF_BASE_COLS)}</div>
      </div>` : ''}

      <div class="stats-grid">
        ${statCard({ icon: 'widgets', label: 'Active Magnets', valueId: 'stat-active-magnets', subId: 'stat-active-magnets-sub', sub: 'Loading…', href: '#/magnets' })}
        ${statCard({ icon: 'ads_click', label: 'Magnet Opens', valueId: 'stat-opens', subId: 'stat-opens-sub', sub: 'Loading…', href: '#/analytics' })}
        ${statCard({ icon: 'person_add', label: 'Leads Captured', valueId: 'stat-leads', subId: 'stat-leads-sub', sub: 'Loading…', href: '#/leads', trendId: 'stat-leads-trend' })}
        ${statCard({ icon: 'check_circle', label: 'Completion Rate', valueId: 'stat-completion', subId: 'stat-completion-sub', sub: 'Loading…', href: '#/analytics' })}
      </div>

      <div class="charts-section">
        ${chartCard('show_chart', 'Opens & Leads', 'dash-trend', { wide: true, controls: true })}
        <div class="charts-grid">
          ${chartCard('filter_alt', 'Completion Funnel', 'dash-funnel')}
          ${chartCard('source', 'Top Campaigns', 'dash-campaigns')}
          ${chartCard('touch_app', 'CTA Usage', 'dash-routes', { full: true })}
        </div>
      </div>

      <div class="content-grid">
        <div class="dashboard-card">
          <div class="card-header">
            <h2 class="card-title"><span class="material-symbols-outlined" aria-hidden="true">bolt</span> Recent Interactions</h2>
            <span class="card-header__aside">
              <span class="live-indicator"><span class="pulse-dot"></span> Live</span>
              <a class="view-all-link" href="#/conversations">View all →</a>
            </span>
          </div>
          <div class="card-body" id="dash-interactions">
            ${emptyState('sensors', 'Loading…')}
          </div>
        </div>
        <div class="dashboard-card">
          <div class="card-header">
            <h2 class="card-title"><span class="material-symbols-outlined" aria-hidden="true">group</span> Recent Leads</h2>
            <a class="view-all-link" href="#/leads">View all →</a>
          </div>
          <div class="card-body" id="recent-leads-body">
            ${emptyState('hourglass_empty', 'Loading leads…')}
          </div>
        </div>
      </div>
    </div>`;
}

export async function init() {
  drillScope = leadsHandoffScope(); drillRange = {};
  const base = AuthController.getConfig?.().BASE_URL || '';
  // Over-quota banner (slice three): the leads cap never blocks capture, so
  // the dashboard says where things stand instead. Self-serve only; fail-open.
  if (AuthController.getUiMode?.() === 'self_serve') {
    // The banner names the NEXT plan and its number (Free sees Pro, Pro sees
    // Enterprise) — "see your plan" asked the user to go find the offer;
    // the offer belongs in the banner. Unknown plan falls back to the
    // generic line rather than a wrong pitch.
    Promise.all([quotaState(), fetchMyPlan()]).then(([q, planData]) => {
      if (!q || q.leads_pct < 80) return;
      const shell = document.querySelector('.page-shell[data-section="home"]');
      if (!shell || document.getElementById('dash-quota-banner')) return;
      const nextKey = { free: 'pro', pro: 'enterprise' }[planData?.plan?.key] || null;
      const next = nextKey ? PLANS[nextKey] : null;
      const offer = next
        ? `<a href="#/billing">Upgrade to ${next.label}</a> for ${Number(next.limits.leads_mo).toLocaleString()} leads a month.`
        : '<a href="#/billing">See your plan</a> for a higher allowance.';
      const div = document.createElement('div');
      div.id = 'dash-quota-banner';
      div.style.cssText = 'margin:0 0 16px;padding:12px 16px;border-radius:12px;border:1px solid var(--color-border, #D4DCE8);background:var(--color-surface, #F4F7FB);font-size:0.9rem;color:var(--color-text, #0B1220);';
      div.innerHTML = q.over
        ? `Your monthly lead allowance is used up. Every lead is still captured, none are lost. ${offer}`
        : `You have used ${q.leads_pct} percent of this month's lead allowance. ${offer}`;
      shell.prepend(div);
    }).catch(() => { /* fail open */ });
  }
  // The portfolio skeleton is already painted at its final height (render());
  // wire its rows now so entering a client never waits on the stats fetch.
  if (inPortfolio()) wireClientRows(document.getElementById('dash-portfolio'));
  document.getElementById('dash-periods')?.addEventListener('click', (e) => {
    const btn = e.target.closest('button[data-days]');
    if (!btn) return;
    periodDays = Number(btn.dataset.days) || 30;
    document.querySelectorAll('#dash-periods .chart-period-btn').forEach((b) => b.classList.toggle('active', Number(b.dataset.days) === periodDays));
    loadStats(base);
  });
  // Delegated on the container, not the rows: loadStats() replaces the card's innerHTML
  // on every period change, so per-row listeners would be dropped and re-added each time.
  document.getElementById('dash-campaigns')?.addEventListener('click', (e) => {
    const row = e.target.closest('[data-campaign]');
    if (!row || e.metaKey || e.ctrlKey || e.shiftKey || e.altKey) return;
    // The <a href> does the navigating; this only parks what /leads should search for.
    setHandoff(HANDOFF.LEADS_SEARCH, row.dataset.campaign);
    dashboardLeads({ ...drillRange, search: row.dataset.campaign });
  });
  document.getElementById('recent-leads-body')?.addEventListener('click', event => {
    const row = event.target.closest('.recent-lead');
    if (!row || event.metaKey || event.ctrlKey || event.shiftKey || event.altKey) return;
    dashboardLeads({ leadId: row.dataset.leadId, leadGuid: row.dataset.leadGuid, ownerGuid: row.dataset.ownerGuid });
  });
  await Promise.all([loadStats(base), loadRecentLeads(base)]);
}

const setText = (id, v) => { const el = document.getElementById(id); if (el) el.textContent = v; };
const setHTML = (id, v) => { const el = document.getElementById(id); if (el) el.innerHTML = v; };
const ymd = (d) => d.toISOString().slice(0, 10);
// Pointer capability, not viewport width — a small laptop window still hovers, and a
// large tablet still doesn't. Guarded: jsdom and older browsers have no matchMedia,
// and defaulting to "has hover" keeps the desktop single-click path in that case.

// One roll-up call → every KPI/chart on the page. Failure degrades to em-dashes, never a crash.
async function loadStats(base) {
  const now = new Date();
  const from = ymd(new Date(now.getTime() - (periodDays - 1) * 86400000));
  const to = ymd(now);
  const portfolio = inPortfolio();
  let s;
  try {
    const groupBy = portfolio ? '&group_by=owner' : '';
    const res = await guardedFetch(`${base}/m5t/v5/dashboard/stats?from=${from}&to=${to}${groupBy}`);
    failIfNotOk(res);
    s = await res.json();
    if (!s.ok) throw new Error('stats not ok');
    drillRange = { from: s.from || from, to: s.to || to };
  } catch (err) {
    console.warn('[dashboard] stats fetch failed:', err);
    ['stat-active-magnets', 'stat-opens', 'stat-leads', 'stat-completion'].forEach((id) => setText(id, '—'));
    ['stat-active-magnets-sub', 'stat-opens-sub', 'stat-leads-sub', 'stat-completion-sub'].forEach((id) => setText(id, 'Unavailable'));
    // The trend chip is set via innerHTML, so the value/sublabel resets above never
    // reach it — clear it explicitly or a chip from the last successful load
    // survives beside "Unavailable".
    const staleTrend = document.getElementById('stat-leads-trend');
    if (staleTrend) { staleTrend.className = 'stat-trend'; staleTrend.innerHTML = ''; }
    // All six panels are fed by THIS request and by nothing else, so the denial's
    // footprint correctly equals the request's — this is slot scoping applied, not a
    // page-level surface. recent-leads-body sits on the same page, is fed by m5mLead and
    // handled separately, and still renders: someone denied stats but holding view_leads
    // sees it, which is what distinguishes "this data is not available to you" from
    // "you are locked out of the dashboard". Compact form so the message repeats six
    // times but the lock glyph does not.
    ['dash-trend', 'dash-funnel', 'dash-campaigns', 'dash-routes', 'dash-interactions', 'dash-portfolio'].forEach((id) =>
      setHTML(id, isDenial(err) ? denialState(undefined, { compact: true }) : emptyState('error', 'Could not load. Please refresh.')));
    return;
  }

  if (portfolio) renderPortfolio(s.by_owner || []);

  // ── KPI tiles ──
  setText('stat-active-magnets', fmtNum(s.magnets.active));
  setText('stat-active-magnets-sub', s.magnets.total > s.magnets.active
    ? `${fmtNum(s.magnets.total - s.magnets.active)} paused` : (s.magnets.active ? 'All running' : 'No magnets yet'));

  const f = s.funnel || {};
  setText('stat-opens', f.opens ? fmtNum(f.opens) : '—');
  setText('stat-opens-sub', f.opens
    ? (f.engagement_rate != null ? `${f.engagement_rate}% engage` : `${fmtNum(f.engaged)} engaged`)
    : 'No opens in this range yet');

  const L = s.leads || {};
  setText('stat-leads', fmtNum(L.total));
  const delta = (L.this_week || 0) - (L.prev_week || 0);
  setText('stat-leads-sub', L.total ? `${fmtNum(L.this_week)} this week` : 'No leads yet');
  // Trend chip: icon + sign so direction is never carried by color alone. Only
  // rendered when there is a real prior week to compare against (prev_week > 0) —
  // the server can't distinguish "no prior week" from "a prior week with zero
  // leads", so an account's first week must never be dressed up as +N growth over
  // a week it did not exist. Zero delta stays neutral: flat icon, plain "0", no sign.
  const trendEl = document.getElementById('stat-leads-trend');
  if (trendEl) {
    if ((L.prev_week || 0) > 0) {
      const dir = delta > 0 ? 'positive' : delta < 0 ? 'negative' : 'neutral';
      const ico = delta > 0 ? 'trending_up' : delta < 0 ? 'trending_down' : 'trending_flat';
      trendEl.className = `stat-trend ${dir}`;
      trendEl.innerHTML = `<span class="material-symbols-outlined" aria-hidden="true">${ico}</span>${delta > 0 ? '+' : ''}${fmtNum(delta)}`;
    } else {
      trendEl.className = 'stat-trend';
      trendEl.innerHTML = '';
    }
  }

  setText('stat-completion', f.completion_rate != null ? `${f.completion_rate}%` : '—');
  setText('stat-completion-sub', f.opens
    ? `${fmtNum(f.completions)} completed of ${fmtNum(f.opens)} opens`
    : 'Appears once magnets get opened');

  // ── Trend: opens (bars) + leads (line) by day ──
  const days = s.by_day || [];
  const anyTrend = days.some((d) => d.opens || d.leads);
  setHTML('dash-trend', anyTrend ? svgTrendChart(days) :
    emptyState('show_chart', 'No activity in this range yet. Opens and leads chart here as visitors engage your magnets.'));
  if (anyTrend) wireTrendTooltip(days);

  // ── Completion funnel ──
  setHTML('dash-funnel', f.opens ? funnelHTML(f) :
    emptyState('filter_alt', 'The funnel fills in once your magnets record opens.'));

  // ── Top campaigns ──
  // Each row drills through to the leads it counts. A real <a href="#/leads"> rather
  // than a click-only div so it keyboard-focuses and middle-clicks like a link; the
  // handler below parks the campaign name for /leads to claim. Opened in a new tab the
  // handoff is not there (fresh module state) and it lands on the unfiltered list —
  // degraded, not broken. data-campaign carries the RAW name; esc() is for the
  // attribute, and the handler reads the decoded value back off the dataset.
  const camps = s.top_campaigns || [];
  setHTML('dash-campaigns', camps.length
    ? `<div class="dash-list">${camps.map((c) => `
        <a class="dash-list__row dash-list__row--link" href="#/leads" data-campaign="${esc(c.campaign)}" title="Search matching leads: ${esc(c.campaign)}" aria-label="Search matching leads: ${esc(c.campaign)}">
          <span class="dash-list__ico"><span class="material-symbols-outlined" aria-hidden="true">campaign</span></span>
          <span class="dash-list__main">${esc(c.campaign)}<small class="dash-list__sub">Search matching leads</small></span>
          <span class="dash-list__num">${fmtNum(c.leads)}</span>
          <span class="dash-list__go material-symbols-outlined" aria-hidden="true">chevron_right</span>
        </a>`).join('')}</div>`
    : emptyState('source', 'No tagged campaigns yet. Leads arriving with ?utm_campaign=… rank here.'));
  {
    const el = document.getElementById('dash-campaigns');
    const list = el?.querySelector('.dash-list');
    if (list) capList(list, { rowSelector: '.dash-list__row', noun: 'campaigns' });
  }

  // ── CTA usage ──
  const ROUTE_META = { book: ['event', 'Book'], chat: ['chat', 'Chat'], call: ['phone_in_talk', 'Call'], email: ['mail', 'Email'], support: ['support_agent', 'Support'] };
  const routes = Object.entries(f.routes || {}).sort((a, b) => b[1] - a[1]);
  const maxClicks = routes.length ? routes[0][1] : 0;
  setHTML('dash-routes', routes.length
    ? `<div class="dash-funnel">${routes.map(([k, n]) => {
        const [icon, label] = ROUTE_META[k] || ['touch_app', k];
        return `<div class="dash-funnel__row">
          <div class="dash-funnel__meta"><span class="dash-funnel__label"><span class="material-symbols-outlined" aria-hidden="true" style="font-size:15px;vertical-align:-3px">${icon}</span> ${esc(label)}</span><span class="dash-funnel__val">${fmtNum(n)}</span></div>
          <div class="dash-funnel__track"><div class="dash-funnel__bar" style="width:${maxClicks ? Math.max(4, Math.round((n / maxClicks) * 100)) : 0}%"></div></div>
        </div>`;
      }).join('')}</div>`
    : emptyState('touch_app', 'CTA taps (Book, Chat, Call, Support) rank here once visitors use the action bar.'));

  // ── Recent interactions ──
  const EVENT_META = {
    open: ['visibility', 'Magnet opened'], engage: ['touch_app', 'Visitor engaged'],
    route: ['alt_route', 'Picked a channel'], complete: ['check_circle', 'Completed: lead captured'],
  };
  const events = s.recent_events || [];
  setHTML('dash-interactions', events.length
    ? `<div class="dash-list">${events.map((ev) => {
        const [icon, label] = EVENT_META[ev.event] || ['sensors', ev.event];
        const detail = ev.event === 'route' && ev.detail ? ` · ${esc(ev.detail)}` : '';
        return `<div class="dash-list__row">
          <span class="dash-list__ico"><span class="material-symbols-outlined" aria-hidden="true">${icon}</span></span>
          <span class="dash-list__main">${label}${detail}</span>
          ${ev.display_mode ? `<span class="dash-list__mode">${esc(ev.display_mode)}</span>` : ''}
          <span class="dash-list__time">${esc(relTime(ev.at))}</span>
        </div>`;
      }).join('')}</div>`
    : emptyState('sensors', "No interactions yet. They'll stream in as visitors engage your magnets."));
  {
    const el = document.getElementById('dash-interactions');
    const list = el?.querySelector('.dash-list');
    if (list) capList(list, { rowSelector: '.dash-list__row', noun: 'interactions' });
  }
}

// ── Portfolio: one row per org in the reseller's book. Clicking a row switches
// the whole app into that client's context (AuthController persists the choice,
// /me refetches, and main.js re-runs the route under the new X-Active-Org).
function renderPortfolio(rows) {
  const el = document.getElementById('dash-portfolio');
  if (!el) return;
  if (!rows.length) {
    el.innerHTML = emptyState('groups', 'No client orgs yet. Create one under Clients to see its numbers here.');
    return;
  }
  const anyAds = rows.some((r) => r.ad);
  const deltaHTML = (r) => {
    const d = (r.leads.this_week || 0) - (r.leads.prev_week || 0);
    if (!r.leads.this_week && !r.leads.prev_week) return '';
    const cls = d >= 0 ? 'dc-delta-up' : 'dc-delta-down';
    return ` <span class="${cls}">(${d >= 0 ? '+' : ''}${fmtNum(d)})</span>`;
  };
  const columns = [
    { h: 'Magnets', td: (r) => `${fmtNum(r.magnets.active)}<span style="color:var(--color-text-subtle,#6C7C9A)">/${fmtNum(r.magnets.total)}</span>` },
    { h: 'Leads · wk', td: (r) => `${fmtNum(r.leads.this_week)}${deltaHTML(r)}` },
    { h: 'Leads · total', td: (r) => fmtNum(r.leads.total) },
    { h: 'Opens', td: (r) => fmtNum(r.opens) },
    { h: 'Compl.', td: (r) => (r.completion_rate != null ? `${r.completion_rate}%` : '—') },
    // A/B activity: computed server-side per org, previously never rendered.
    ...(rows.some((r) => r.ab_tests?.running || r.ab_tests?.completed) ? [
      { h: 'A/B', td: (r) => r.ab_tests?.running
          ? `${fmtNum(r.ab_tests.running)} <span class="dc-sub">running</span>`
          : (r.ab_tests?.completed ? `${fmtNum(r.ab_tests.completed)} <span class="dc-sub">done</span>` : '—') },
    ] : []),
    ...(rows.some((r) => r.booked) ? [
      { h: 'Booked', td: (r) => (r.booked ? fmtNum(r.booked) : '—') },
    ] : []),
    ...(rows.some((r) => r.call_taps) ? [
      { h: 'Call taps', td: (r) => (r.call_taps ? fmtNum(r.call_taps) : '—') },
    ] : []),
    // CTA clicks = visitors who clicked the outbound button of an informational step
    // (a flow that captures nothing converts here instead of at Completed).
    ...(rows.some((r) => r.cta_clicks) ? [
      { h: 'CTA clicks', td: (r) => (r.cta_clicks ? fmtNum(r.cta_clicks) : '—') },
    ] : []),
    ...(rows.some((r) => r.called) ? [
      { h: 'Called', td: (r) => (r.called ? fmtNum(r.called) : '—') },
    ] : []),
    ...(anyAds ? [
      { h: 'Spend', td: (r) => (r.ad ? `$${fmtNum(r.ad.spend)}` : '—') },
      { h: 'CPL', td: (r) => (r.ad?.cost_per_lead != null ? `$${fmtNum(r.ad.cost_per_lead)}` : '—') },
    ] : []),
  ];
  el.innerHTML = clientsTableHTML(rows, columns);
  wireClientRows(el);
}

function funnelHTML(f) {
  const stages = [
    ['Opened', f.opens, ''],
    ['Engaged', f.engaged, ' dash-funnel__bar--mid'],
    ['Completed', f.completions, ' dash-funnel__bar--end'],
    // Booked/Call taps/Called appear only once those signals exist — no dead
    // stages for orgs that never produce them. Call taps = visitors who tapped
    // or copied the magnet's phone number (intent); Called = verified calls
    // from call tracking.
    ...(f.booked ? [['Booked', f.booked, ' dash-funnel__bar--end']] : []),
    ...(f.call_taps ? [['Call taps', f.call_taps, ' dash-funnel__bar--end']] : []),
    ...(f.cta_clicks ? [['CTA clicks', f.cta_clicks, ' dash-funnel__bar--end']] : []),
    ...(f.called ? [['Called', f.called, ' dash-funnel__bar--end']] : []),
  ];
  const max = Math.max(1, f.opens || 0);
  // Capped at 100: Booked comes off webhook bookings, which are not bounded by
  // the window's opens the way the beaconed stages are.
  const pct = (n) => Math.min(100, Math.round(((n || 0) / max) * 100));
  return `<div class="dash-funnel">${stages.map(([label, n, cls]) => `
    <div class="dash-funnel__row">
      <div class="dash-funnel__meta">
        <span class="dash-funnel__label">${label}</span>
        <span class="dash-funnel__val">${fmtNum(n)} <span class="dash-funnel__pct">${pct(n)}%</span></span>
      </div>
      <div class="dash-funnel__track"><div class="dash-funnel__bar${cls}" style="width:${Math.max(2, pct(n))}%"></div></div>
    </div>`).join('')}</div>`;
}

// Recent Leads list — person-level rows still come straight from owner-scoped m5mLead.
async function loadRecentLeads(base) {
  const listEl = document.getElementById('recent-leads-body');
  if (!listEl) return;
  try {
    // Only five rows are rendered, and the endpoint already returns newest-first, so ask
    // for five instead of transporting the whole lead history just to slice it here.
    // (The status filter below is belt-and-braces: the endpoint reads a view that already
    // excludes DELETED/ARCHIVED, so it cannot shrink this page below five.)
    const res = await guardedFetch(`${base}/m5t/v5/m5mLead?limit=5`);
    failIfNotOk(res);
    const json = await res.json();
    const leads = (Array.isArray(json?.data) ? json.data : []).filter((l) => !['DELETED', 'ARCHIVED'].includes(l.status));
    listEl.innerHTML = leads.length
      ? leads.slice(0, 5).map(recentLeadHTML).join('')
      : emptyState('contacts', 'No leads captured yet. Publish a magnet to start collecting.');
  } catch (err) {
    console.warn('[dashboard] Leads fetch failed:', err);
    listEl.innerHTML = isDenial(err) ? denialState() : emptyState('error', 'Could not load recent leads.');
  }
}

// Round up to a "nice" axis max (1/2/5 × 10^n).

// Dual-axis trend: opens bars (left axis) + leads line (right axis).
// The SVG scaffold, the marks, the hit bands and every colour now come from
// services/chart-kit.js — this was a 40-line copy of the same chart ad-performance also
// had a copy of, complete with its own duplicate niceMax/fmtAxis.
export function svgTrendChart(rows) {
  return dualAxisChartHTML({
    rows,
    xLabel: (r) => String(r.date || '').slice(5),
    left: { value: (r) => r.opens || 0, label: 'Magnet Opens' },
    right: { value: (r) => r.leads || 0, label: 'Leads' },
    ariaLabel: 'Magnet opens versus leads over time',
    // A day with no leads drills into a guaranteed-empty list; the count was the answer.
    activatable: (r) => Number(r.leads || 0) > 0,
    pointLabel: (r) => `${trendDateLabel(r.date)}: ${fmtNum(r.opens || 0)} opens, ${fmtNum(r.leads || 0)} leads`,
    // Bands stay self-describing. The handlers read the rows closure, but keeping the day
    // and count on the element is what makes the chart debuggable from the DOM.
    hitData: (r) => ({ day: r.date, leads: Number(r.leads || 0) }),
  });
}

function trendDateLabel(iso) {
  try {
    return new Date(`${iso}T00:00:00`).toLocaleDateString(undefined, { month: 'short', day: 'numeric', year: 'numeric' });
  } catch { return iso; }
}

// Hover, focus, keyboard and drill-through for the trend chart. The behaviour lives in
// chart-kit's wireChartHover — delegated (so a period switch that repaints the chart does
// not need listeners re-attached) and keyboard-operable, which this chart was not: the hit
// bands had no tabindex and no key handling, so its drill-through to /leads could only be
// reached with a pointer.
export function wireTrendTooltip(days) {
  const host = document.getElementById('dash-trend');
  if (!host) return () => {};
  return wireChartHover(host, {
    rows: days,
    tipHTML: (d) => `
      <div class="m5c-tip__title">${trendDateLabel(d.date)}</div>
      <div class="m5c-tip__row"><i class="m5c-sw m5c-sw--1"></i>${fmtNum(d.opens || 0)} opens</div>
      <div class="m5c-tip__row"><i class="m5c-sw m5c-sw--2"></i>${fmtNum(d.leads || 0)} leads</div>
      ${Number(d.leads || 0) > 0 ? '<div class="m5c-tip__go">Open these leads →</div>' : ''}`,
    onActivate: (d) => {
      setHandoff(HANDOFF.LEADS_DAY, d.date);
      dashboardLeads({ day: d.date });
      window.location.hash = '#/leads';
    },
  });
}

export default { render, init };
